import React, { useState } from "react";
import { ChevronUpIcon, ChevronDownIcon } from "@heroicons/react/24/outline";

const Table = ({
  title,
  columns,
  data,
  renderRow,
  emptyMessage = "No data available",
  className = "",
  sortableColumns = [],
  columnKeys = [],
}: any) => {
  const [sortConfig, setSortConfig] = useState<{
    columnIndex: number;
    direction: "asc" | "desc";
  } | null>(null);

  if (!Array.isArray(data)) {
    data = [data];
  }

  const handleSort = (columnIndex: number) => {
    if (!sortableColumns.includes(columnIndex)) return;

    let direction: "asc" | "desc" = "asc";

    if (
      sortConfig &&
      sortConfig.columnIndex === columnIndex &&
      sortConfig.direction === "asc"
    ) {
      direction = "desc";
    }

    setSortConfig({ columnIndex, direction });
  };

  const sortedData = React.useMemo(() => {
    if (!sortConfig || !data) return data;

    return [...data].sort((a, b) => {
      const key = columnKeys[sortConfig.columnIndex];
      const aValue = a[key];
      const bValue = b[key];

      // Handle numeric values
      if (typeof aValue === "number" && typeof bValue === "number") {
        return sortConfig.direction === "asc"
          ? aValue - bValue
          : bValue - aValue;
      }

      // Handle string values
      const aStr = String(aValue || "").toLowerCase();
      const bStr = String(bValue || "").toLowerCase();

      if (aStr < bStr) return sortConfig.direction === "asc" ? -1 : 1;
      if (aStr > bStr) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [data, sortConfig, columnKeys]);

  if (Array.isArray(data) && (!data || data.length === 0)) {
    return (
      <div
        className={`p-4 bg-[#2E4051] rounded text-center text-white ${className}`}
      >
        {emptyMessage}
      </div>
    );
  }

  return (
    <div className="">
      <div className=" rounded-lg border border-[#374B5E]">
        <table className="min-w-full border-collapse rounded-lg">

          <thead className="rounded-t-xl sticky top-0 z-10">

            <tr className={`bg-[#222F3C] text-[#889DB2] border-b-2 border-[#374B5E] rounded-t-lg ${className}`}>
              {columns.map((column: any, index: number) => (
                <th
                  key={index}
                  className={`px-4 py-3  text-base font-normal ${
                    sortableColumns.includes(index) ? 'cursor-pointer hover:text-[#55B7E4] select-none' : ''
                  } `}
                  onClick={() => handleSort(index)}
                >
                  <div className={`flex  items-center ${className} gap-2`}>
                    {column}
                    {sortableColumns.includes(index) && (
                      <div className="flex flex-col">
                        <ChevronUpIcon
                          className={`w-3 h-3 ${
                            sortConfig?.columnIndex === index && sortConfig.direction === 'asc'
                              ? 'text-[#ffffff]'
                              : 'text-[#889DB2]'
                          }`}
                        />
                        <ChevronDownIcon
                          className={`w-3 h-3 -mt-1 ${
                            sortConfig?.columnIndex === index && sortConfig.direction === 'desc'
                              ? 'text-[#008564]'
                              : 'text-[#889DB2]'
                          }`}
                        />
                      </div>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>

          <tbody className="bg-[#2E4051] text-white rounded-b-lg">
            {sortedData.map((item: any, index: number) => renderRow(item, index))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Table;
